import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Paper,
    Alert,
    Stack,
    Chip,
    Card,
    CardContent,
    useTheme,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import AccountSelector from '../common/AccountSelector';
import CostCenterSelector from '../common/CostCenterSelector';
import {
    CreateVoucherDto,
    CreateVoucherDetailDto,
    VoucherDto,
    UpdateVoucherDto,
} from '../../types/vouchers.types';
import { VOUCHER_SIDE } from '../../constants/vouchers.constants';
import checkPaymentsService from '../../services/checkPayments.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

interface CheckPaymentDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (dto: CreateVoucherDto | UpdateVoucherDto) => Promise<void>;
    editVoucher?: VoucherDto | null;
    checksParentAccountId: number; // حساب البنوك الرئيسي
}

interface DebitLine {
    id: string;
    accountId: number | null;
    costCenterId: number | null;
    amount: number;
    description: string;
}

const CheckPaymentDialog: React.FC<CheckPaymentDialogProps> = ({
    open,
    onClose,
    onSave,
    editVoucher,
    checksParentAccountId,
}) => {
    const { language } = useLanguage();
    const isRTL = language === 'ar';
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { minDate, maxDate } = useFiscalDateRange();

    const gridRef = useRef<HTMLDivElement>(null);
    useGridEnterNavigation(gridRef);

    // Form State
    const [voucherNumber, setVoucherNumber] = useState<string>('');
    const [voucherDate, setVoucherDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [payeeName, setPayeeName] = useState<string>('');
    const [notes, setNotes] = useState<string>('');

    // Check Info
    const [chequeNumber, setChequeNumber] = useState<string>('');
    const [chequeDate, setChequeDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [bankName, setBankName] = useState<string>('');

    // Credit Section (Single Line - Checks/Bank)
    const [creditAccountId, setCreditAccountId] = useState<number | null>(null);
    const [creditAmount, setCreditAmount] = useState<number>(0);

    // Debit Section (Multiple Lines)
    const [debitLines, setDebitLines] = useState<DebitLine[]>([
        {
            id: Math.random().toString(),
            accountId: null,
            costCenterId: null,
            amount: 0,
            description: '',
        },
    ]);

    const [loading, setLoading] = useState(false);
    const [checkingNumber, setCheckingNumber] = useState(false);
    const [error, setError] = useState<string>('');

    // Generate auto voucher number
    const generateAutoNumber = async () => {
        try {
            const year = new Date().getFullYear();
            const vouchers = await checkPaymentsService.getAll();

            const currentYearVouchers = vouchers.filter((v) =>
                v.voucherNumber.startsWith(`CHP-${year}-`)
            );

            let maxNum = 0;
            currentYearVouchers.forEach((v) => {
                const parts = v.voucherNumber.split('-');
                if (parts.length === 3) {
                    const num = parseInt(parts[2]);
                    if (!isNaN(num) && num > maxNum) {
                        maxNum = num;
                    }
                }
            });

            const nextNum = (maxNum + 1).toString().padStart(4, '0');
            return `CHP-${year}-${nextNum}`;
        } catch (err) {
            console.error('Failed to generate auto number:', err);
            return `CHP-${new Date().getFullYear()}-0001`;
        }
    };

    // Check if voucher number exists
    const checkVoucherNumberExists = async (
        number: string
    ): Promise<boolean> => {
        try {
            const vouchers = await checkPaymentsService.getAll();
            return vouchers.some(
                (v) =>
                    v.voucherNumber === number &&
                    v.voucherId !== editVoucher?.voucherId
            );
        } catch (err) {
            console.error('Failed to check voucher number:', err);
            return false;
        }
    };

    // Reset form
    const resetForm = () => {
        setVoucherNumber('');
        setVoucherDate(new Date().toISOString().split('T')[0]);
        setPayeeName('');
        setNotes('');
        setChequeNumber('');
        setChequeDate(new Date().toISOString().split('T')[0]);
        setBankName('');
        setCreditAccountId(null);
        setCreditAmount(0);
        setDebitLines([
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
        setError('');
    };

    // Load edit data
    const loadEditData = (voucher: VoucherDto) => {
        setVoucherNumber(voucher.voucherNumber);
        setVoucherDate(voucher.voucherDate.split('T')[0]);
        setPayeeName(voucher.payeeName || '');
        setNotes(voucher.notes || '');
        setChequeNumber(voucher.chequeNumber || '');
        setChequeDate(
            voucher.chequeDate
                ? voucher.chequeDate.split('T')[0]
                : new Date().toISOString().split('T')[0]
        );
        setBankName(voucher.bankName || '');

        const creditDetail = voucher.details.find(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.CREDIT.toLowerCase()
        );

        if (creditDetail) {
            setCreditAccountId(creditDetail.accountId || null);
            setCreditAmount(creditDetail.allocatedAmount);
        }

        const debitDetails = voucher.details.filter(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.DEBIT.toLowerCase()
        );

        if (debitDetails.length > 0) {
            setDebitLines(
                debitDetails.map((d) => ({
                    id: Math.random().toString(),
                    accountId: d.accountId || null,
                    costCenterId: d.costCenterId || null,
                    amount: d.allocatedAmount,
                    description: d.description || '',
                }))
            );
        }
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (open) {
            if (editVoucher) {
                timer = setTimeout(() => loadEditData(editVoucher), 100);
            } else {
                resetForm();
                generateAutoNumber().then(setVoucherNumber);
            }
        }
        return () => clearTimeout(timer);
    }, [open, editVoucher?.voucherId]);

    const handleRegenerateNumber = async () => {
        const newNumber = await generateAutoNumber();
        setVoucherNumber(newNumber);
    };

    // Debit Lines Management
    const addDebitLine = () => {
        setDebitLines([
            ...debitLines,
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
    };

    const removeDebitLine = (id: string) => {
        if (debitLines.length > 1) {
            setDebitLines(debitLines.filter((line) => line.id !== id));
        }
    };

    const updateDebitLine = (
        id: string,
        field: keyof DebitLine,
        value: any
    ) => {
        setDebitLines(
            debitLines.map((line) =>
                line.id === id ? { ...line, [field]: value } : line
            )
        );
    };

    // Check if account is already used
    const isAccountUsed = (
        accountId: number | null,
        currentLineId?: string
    ): boolean => {
        if (!accountId) return false;
        if (accountId === creditAccountId) return true;
        return debitLines.some(
            (line) => line.id !== currentLineId && line.accountId === accountId
        );
    };

    // Calculations
    const totalDebit = debitLines.reduce(
        (sum, line) => sum + (line.amount || 0),
        0
    );
    const totalCredit = creditAmount || 0;
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    const difference = totalDebit - totalCredit;

    // Auto-update credit amount
    useEffect(() => {
        setCreditAmount(totalDebit);
    }, [totalDebit]);

    // Validation
    const validate = async (): Promise<boolean> => {
        if (!voucherNumber.trim()) {
            setError(isRTL ? 'رقم السند مطلوب' : 'Voucher number is required');
            return false;
        }

        setCheckingNumber(true);
        const exists = await checkVoucherNumberExists(voucherNumber);
        setCheckingNumber(false);

        if (exists) {
            setError(
                isRTL
                    ? 'رقم السند مستخدم مسبقاً'
                    : 'Voucher number already exists'
            );
            return false;
        }

        if (!voucherDate) {
            setError(isRTL ? 'التاريخ مطلوب' : 'Date is required');
            return false;
        }

        if (!payeeName.trim()) {
            setError(isRTL ? 'اسم المدفوع له مطلوب' : 'Payee name is required');
            return false;
        }

        if (!chequeNumber.trim()) {
            setError(isRTL ? 'رقم الشيك مطلوب' : 'Check number is required');
            return false;
        }

        if (!chequeDate) {
            setError(isRTL ? 'تاريخ الشيك مطلوب' : 'Check date is required');
            return false;
        }

        if (!bankName.trim()) {
            setError(isRTL ? 'اسم البنك مطلوب' : 'Bank name is required');
            return false;
        }

        if (!creditAccountId) {
            setError(
                isRTL
                    ? 'حساب البنك (الشيكات) مطلوب'
                    : 'Bank account (checks) is required'
            );
            return false;
        }

        if (totalCredit <= 0) {
            setError(
                isRTL
                    ? 'المبلغ يجب أن يكون أكبر من صفر'
                    : 'Amount must be greater than zero'
            );
            return false;
        }

        if (!isBalanced) {
            setError(
                isRTL ? 'المدين يجب أن يساوي الدائن' : 'Debit must equal Credit'
            );
            return false;
        }

        const invalidDebitLines = debitLines.filter(
            (line) => !line.accountId || line.amount <= 0
        );
        if (invalidDebitLines.length > 0) {
            setError(
                isRTL
                    ? 'جميع الأسطر يجب أن تحتوي على حساب ومبلغ'
                    : 'All lines must have account and amount'
            );
            return false;
        }
        for (const line of debitLines) {
            if (line.accountId) {
                try {
                    const accountResponse = await apiClient.get(
                        '/ChartOfAccounts/' + line.accountId
                    );
                    const account = accountResponse.data.data;
                    if (
                        account &&
                        account.requireCostCenter &&
                        !line.costCenterId
                    ) {
                        const msg =
                            (isRTL ? 'الحساب' : 'Account') +
                            ' "' +
                            (isRTL
                                ? account.accountNameAr
                                : account.accountName) +
                            '" ' +
                            (isRTL
                                ? 'يتطلب مركز تكلفة'
                                : 'requires Cost Center');
                        setError(msg);
                        toast.warning(msg);
                        return false;
                    }
                } catch (err) {
                    console.error('Error validating account:', err);
                }
            }
        }

        setError('');
        return true;
    };

    // Save
    const handleSave = async () => {
        if (!(await validate())) return;

        try {
            setLoading(true);
            setError('');

            const details: CreateVoucherDetailDto[] = [];

            // Credit Side (Bank)
            details.push({
                allocatedAmount: creditAmount,
                accountId: creditAccountId,
                side: VOUCHER_SIDE.CREDIT,
                costCenterId: null,
                description: `${isRTL ? 'شيك رقم' : 'Check No.'} ${chequeNumber} - ${bankName}`,
            });

            // Debit Side (Multiple)
            debitLines.forEach((line) => {
                details.push({
                    allocatedAmount: line.amount,
                    accountId: line.accountId,
                    side: VOUCHER_SIDE.DEBIT,
                    costCenterId: line.costCenterId,
                    description: line.description,
                });
            });

            if (editVoucher) {
                const updateDto: UpdateVoucherDto = {
                    voucherId: editVoucher.voucherId,
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CHP',
                    payeeName: payeeName.trim(),
                    amount: totalCredit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    chequeNumber,
                    chequeDate,
                    bankName,
                    notes,
                    details,
                };
                await onSave(updateDto);
            } else {
                const createDto: CreateVoucherDto = {
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CHP',
                    payeeName: payeeName.trim(),
                    amount: totalCredit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    chequeNumber,
                    chequeDate,
                    bankName,
                    notes,
                    details,
                };
                await onSave(createDto);
            }

            toast.success(isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully');
            handleClose();
        } catch (err: any) {
            const errorMsg =
                err.message || (isRTL ? 'حدث خطأ' : 'An error occurred');
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            dir={isRTL ? 'rtl' : 'ltr'}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)'
                        : 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)',
                    height: '95vh',
                    border: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)'
                        : 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2.5,
                    borderBottom: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PaymentIcon sx={{ fontSize: 36 }} />
                    <Box
                        component="span"
                        sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                    >
                        {editVoucher
                            ? isRTL
                                ? 'تعديل سند صرف شيك'
                                : 'Edit Check Payment'
                            : isRTL
                              ? 'سند صرف شيك جديد'
                              : 'New Check Payment'}
                    </Box>
                </Box>
                <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ mt: 3, overflowY: 'auto' }}>
                <Stack spacing={3}>
                    {/* Error Alert */}
                    {error && (
                        <Alert
                            severity="error"
                            onClose={() => setError('')}
                            sx={{ borderRadius: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Voucher Info Card */}
                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: 3,
                            bgcolor: isDarkMode
                                ? 'rgba(255,255,255,0.05)'
                                : 'white',
                            border: isDarkMode
                                ? '1px solid rgba(255,255,255,0.1)'
                                : 'none',
                            boxShadow: isDarkMode
                                ? 'none'
                                : '0 8px 32px rgba(244, 67, 54, 0.15)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <ReceiptIcon
                                    sx={{
                                        color: isDarkMode
                                            ? '#ff8a80'
                                            : '#f44336',
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: isDarkMode
                                            ? '#ff8a80'
                                            : '#f44336',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {isRTL
                                        ? 'معلومات السند'
                                        : 'Voucher Information'}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ flex: '1 1 45%', minWidth: 250 }}>
                                    <TextField
                                        label={
                                            isRTL
                                                ? 'رقم السند'
                                                : 'Voucher Number'
                                        }
                                        value={voucherNumber}
                                        onChange={(e) =>
                                            setVoucherNumber(e.target.value)
                                        }
                                        fullWidth
                                        required
                                        placeholder="CHP-2026-0001"
                                        InputProps={{
                                            endAdornment: !editVoucher && (
                                                <IconButton
                                                    size="small"
                                                    onClick={
                                                        handleRegenerateNumber
                                                    }
                                                    title={
                                                        isRTL
                                                            ? 'إعادة توليد الرقم'
                                                            : 'Regenerate Number'
                                                    }
                                                >
                                                    <RefreshIcon />
                                                </IconButton>
                                            ),
                                        }}
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'white',
                                            '& .MuiInputLabel-root': {
                                                color: isDarkMode
                                                    ? 'rgba(255,255,255,0.7)'
                                                    : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: isDarkMode
                                                    ? 'white'
                                                    : 'inherit',
                                            },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ flex: '1 1 45%', minWidth: 250 }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label={isRTL ? 'التاريخ' : 'Date'}
                                            value={voucherDate ? new Date(voucherDate) : null}
                                            onChange={(date) => setVoucherDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                            format="dd/MM/yyyy"
                                            views={['year', 'month', 'day']}
                                            openTo="day"
                                            minDate={minDate}
                                            maxDate={maxDate}
                                            slotProps={{ textField: { fullWidth: true, required: true, sx: { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'white', '& .MuiInputLabel-root': { color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'inherit' }, '& .MuiOutlinedInput-root': { color: isDarkMode ? 'white' : 'inherit' } } } }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                <Box sx={{ flex: '1 1 100%' }}>
                                    <TextField
                                        label={isRTL ? 'المدفوع له' : 'Paid To'}
                                        value={payeeName}
                                        onChange={(e) =>
                                            setPayeeName(e.target.value)
                                        }
                                        fullWidth
                                        required
                                        placeholder={
                                            isRTL
                                                ? 'أدخل اسم الشخص أو الشركة المدفوع لها'
                                                : 'Enter payee name or company'
                                        }
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'white',
                                            '& .MuiInputLabel-root': {
                                                color: isDarkMode
                                                    ? 'rgba(255,255,255,0.7)'
                                                    : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: isDarkMode
                                                    ? 'white'
                                                    : 'inherit',
                                            },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ flex: '1 1 100%' }}>
                                    <TextField
                                        label={isRTL ? 'ملاحظات' : 'Notes'}
                                        value={notes}
                                        onChange={(e) =>
                                            setNotes(e.target.value)
                                        }
                                        fullWidth
                                        multiline
                                        rows={2}
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'white',
                                            '& .MuiInputLabel-root': {
                                                color: isDarkMode
                                                    ? 'rgba(255,255,255,0.7)'
                                                    : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: isDarkMode
                                                    ? 'white'
                                                    : 'inherit',
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Check Info Card */}
                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: 3,
                            bgcolor: isDarkMode
                                ? 'rgba(255,255,255,0.05)'
                                : 'white',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(255,138,128,0.5)'
                                : '#ff8a80',
                            boxShadow: isDarkMode
                                ? 'none'
                                : '0 8px 32px rgba(255,138,128, 0.25)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <CheckCircleOutlineIcon
                                    sx={{
                                        color: isDarkMode
                                            ? '#ff8a80'
                                            : '#f44336',
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: isDarkMode
                                            ? '#ff8a80'
                                            : '#f44336',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {isRTL
                                        ? '💳 معلومات الشيك'
                                        : '💳 Check Information'}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                                    <TextField
                                        label={
                                            isRTL ? 'رقم الشيك' : 'Check Number'
                                        }
                                        value={chequeNumber}
                                        onChange={(e) =>
                                            setChequeNumber(e.target.value)
                                        }
                                        fullWidth
                                        required
                                        placeholder="123456"
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'white',
                                            '& .MuiInputLabel-root': {
                                                color: isDarkMode
                                                    ? 'rgba(255,255,255,0.7)'
                                                    : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: isDarkMode
                                                    ? 'white'
                                                    : 'inherit',
                                            },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label={isRTL ? 'تاريخ الشيك' : 'Check Date'}
                                            value={chequeDate ? new Date(chequeDate) : null}
                                            onChange={(date) => setChequeDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                            format="dd/MM/yyyy"
                                            views={['year', 'month', 'day']}
                                            openTo="day"
                                            slotProps={{ textField: { fullWidth: true, required: true, sx: { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'white', '& .MuiInputLabel-root': { color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'inherit' }, '& .MuiOutlinedInput-root': { color: isDarkMode ? 'white' : 'inherit' } } } }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                                    <TextField
                                        label={
                                            isRTL ? 'اسم البنك' : 'Bank Name'
                                        }
                                        value={bankName}
                                        onChange={(e) =>
                                            setBankName(e.target.value)
                                        }
                                        fullWidth
                                        required
                                        placeholder={
                                            isRTL
                                                ? 'البنك الوطني'
                                                : 'National Bank'
                                        }
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'white',
                                            '& .MuiInputLabel-root': {
                                                color: isDarkMode
                                                    ? 'rgba(255,255,255,0.7)'
                                                    : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: isDarkMode
                                                    ? 'white'
                                                    : 'inherit',
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Credit Section (Bank Account - Single Line) */}
                    <Card
                        elevation={5}
                        sx={{
                            borderRadius: 3,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(244,67,54,0.15) 0%, rgba(211,47,47,0.15) 100%)'
                                : 'linear-gradient(135deg, #ffccbc 0%, #ffab91 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(244,67,54,0.5)'
                                : '#f44336',
                            boxShadow: isDarkMode
                                ? 'none'
                                : '0 8px 32px rgba(244, 67, 54, 0.25)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <AccountBalanceIcon
                                    sx={{
                                        color: isDarkMode
                                            ? '#ff8a80'
                                            : '#f44336',
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color: isDarkMode
                                            ? '#ff8a80'
                                            : '#f44336',
                                    }}
                                >
                                    {isRTL
                                        ? '📤 الطرف الدائن (حساب البنك)'
                                        : '📤 Credit Side (Bank Account)'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <AccountSelector
                                        value={creditAccountId}
                                        onChange={setCreditAccountId}
                                        label={
                                            isRTL
                                                ? 'حساب البنك (الشيك)'
                                                : 'Bank Account (Check)'
                                        }
                                        required
                                        parentAccountId={checksParentAccountId}
                                        onlyFinalAccounts={true}
                                    />
                                </Box>
                                <TextField
                                    label={isRTL ? 'المبلغ' : 'Amount'}
                                    type="number"
                                    value={creditAmount}
                                    disabled
                                    sx={{
                                        width: 200,
                                        '& .MuiInputBase-root': {
                                            bgcolor: isDarkMode
                                                ? 'rgba(244,67,54,0.2)'
                                                : 'rgba(255,255,255,0.9)',
                                            color: isDarkMode
                                                ? '#ff8a80'
                                                : '#f44336',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                        },
                                    }}
                                    InputProps={{
                                        inputProps: { min: 0, step: 0.001 },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Debit Section (Multiple Lines) */}
                    <Card
                        elevation={5}
                        sx={{
                            borderRadius: 3,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(21,101,192,0.15) 0%, rgba(13,71,161,0.15) 100%)'
                                : 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(79,195,247,0.5)'
                                : '#1976d2',
                            boxShadow: isDarkMode
                                ? 'none'
                                : '0 8px 32px rgba(25, 118, 210, 0.25)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <PendingActionsIcon
                                        sx={{
                                            color: isDarkMode
                                                ? '#4fc3f7'
                                                : '#1976d2',
                                            fontSize: 28,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color: isDarkMode
                                                ? '#4fc3f7'
                                                : '#1976d2',
                                        }}
                                    >
                                        {isRTL
                                            ? '📥 الطرف المدين (إلى)'
                                            : '📥 Debit Side (To)'}
                                    </Typography>
                                </Box>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addDebitLine}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        background: isDarkMode
                                            ? 'linear-gradient(45deg, #c62828 30%, #b71c1c 90%)'
                                            : 'linear-gradient(45deg, #f44336 30%, #e91e63 90%)',
                                        boxShadow:
                                            '0 3px 5px 2px rgba(244, 67, 54, .3)',
                                    }}
                                >
                                    {isRTL ? 'إضافة سطر' : 'Add Line'}
                                </Button>
                            </Box>

                            <Box ref={gridRef}>
                            <Stack spacing={2}>
                                {debitLines.map((line, index) => (
                                    <Paper
                                        key={line.id}
                                        elevation={3}
                                        sx={{
                                            p: 2,
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(255,255,255,0.9)',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'divider',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Chip
                                                label={index + 1}
                                                color="primary"
                                                sx={{
                                                    minWidth: 40,
                                                    fontWeight: 'bold',
                                                    bgcolor: isDarkMode
                                                        ? 'rgba(79,195,247,0.3)'
                                                        : '#1976d2',
                                                    color: isDarkMode
                                                        ? '#4fc3f7'
                                                        : 'white',
                                                }}
                                            />

                                            <Box sx={{ flex: 2 }}>
                                                <AccountSelector
                                                    value={line.accountId}
                                                    onChange={(value) => {
                                                        if (
                                                            value &&
                                                            isAccountUsed(
                                                                value,
                                                                line.id
                                                            )
                                                        ) {
                                                            alert(
                                                                isRTL
                                                                    ? 'هذا الحساب مستخدم بالفعل!'
                                                                    : 'This account is already used!'
                                                            );
                                                            return;
                                                        }
                                                        updateDebitLine(
                                                            line.id,
                                                            'accountId',
                                                            value
                                                        );
                                                    }}
                                                    label={
                                                        isRTL
                                                            ? 'الحساب'
                                                            : 'Account'
                                                    }
                                                    required
                                                    onlyFinalAccounts={true}
                                                />
                                            </Box>

                                            <Box sx={{ flex: 1 }}>
                                                <CostCenterSelector
                                                    value={line.costCenterId}
                                                    onChange={(value) =>
                                                        updateDebitLine(
                                                            line.id,
                                                            'costCenterId',
                                                            value
                                                        )
                                                    }
                                                    label={
                                                        isRTL
                                                            ? 'مركز التكلفة'
                                                            : 'Cost Center'
                                                    }
                                                />
                                            </Box>

                                            <TextField
                                                label={
                                                    isRTL ? 'المبلغ' : 'Amount'
                                                }
                                                type="number"
                                                value={line.amount || ''}
                                                onChange={(e) =>
                                                    updateDebitLine(
                                                        line.id,
                                                        'amount',
                                                        parseFloat(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                                required
                                                sx={{
                                                    width: 150,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                                InputProps={{
                                                    inputProps: {
                                                        min: 0,
                                                        step: 0.001,
                                                    },
                                                }}
                                            />

                                            <TextField
                                                label={
                                                    isRTL
                                                        ? 'الوصف'
                                                        : 'Description'
                                                }
                                                value={line.description}
                                                onChange={(e) =>
                                                    updateDebitLine(
                                                        line.id,
                                                        'description',
                                                        e.target.value
                                                    )
                                                }
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                            />

                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    removeDebitLine(line.id)
                                                }
                                                disabled={
                                                    debitLines.length === 1
                                                }
                                                sx={{ mt: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Totals Card */}
                    <Card
                        elevation={6}
                        sx={{
                            borderRadius: 3,
                            background: isBalanced
                                ? isDarkMode
                                    ? 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)'
                                    : 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)'
                                : isDarkMode
                                  ? 'linear-gradient(135deg, #c31432 0%, #240b36 100%)'
                                  : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            color: 'white',
                            boxShadow: isBalanced
                                ? '0 10px 40px rgba(244, 67, 54, 0.4)'
                                : '0 10px 40px rgba(250, 112, 154, 0.4)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Typography variant="h5" fontWeight="bold">
                                    {isRTL ? '⚖️ الإجمالي' : '⚖️ Total'}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 3,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'المدين' : 'Debit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalDebit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight="bold">
                                        =
                                    </Typography>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'الدائن' : 'Credit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalCredit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h2" fontWeight="bold">
                                        {isBalanced ? '✅' : '❌'}
                                    </Typography>
                                </Box>
                            </Box>
                            {!isBalanced && Math.abs(difference) > 0.01 && (
                                <Alert
                                    severity="warning"
                                    sx={{
                                        mt: 2,
                                        bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.15)'
                                            : 'rgba(255,255,255,0.9)',
                                        color: isDarkMode ? 'white' : 'inherit',
                                    }}
                                >
                                    {isRTL ? 'الفرق: ' : 'Difference: '}
                                    <strong>
                                        {Math.abs(difference).toFixed(3)}
                                    </strong>
                                    {difference > 0
                                        ? isRTL
                                            ? ' (المدين أكبر)'
                                            : ' (Debit is higher)'
                                        : isRTL
                                          ? ' (الدائن أكبر)'
                                          : ' (Credit is higher)'}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.100',
                    borderTop: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    size="large"
                    sx={{
                        minWidth: 120,
                        color: isDarkMode ? 'white' : 'inherit',
                    }}
                >
                    {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !isBalanced || checkingNumber}
                    size="large"
                    startIcon={loading || checkingNumber ? null : <SaveIcon />}
                    sx={{
                        minWidth: 120,
                        background: isDarkMode
                            ? 'linear-gradient(45deg, #c62828 30%, #b71c1c 90%)'
                            : 'linear-gradient(45deg, #f44336 30%, #e91e63 90%)',
                        boxShadow: '0 3px 5px 2px rgba(244, 67, 54, .3)',
                    }}
                >
                    {loading || checkingNumber
                        ? isRTL
                            ? 'جارٍ المعالجة...'
                            : 'Processing...'
                        : isRTL
                          ? 'حفظ'
                          : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CheckPaymentDialog;
